import React from "react";
import { withRouter, Button, RouterProps } from "component-library";
import AgencyService from "services/AgencyService";
import { GlobalStorage } from "common";
import { PROFILE_CONTACT_FULL_PATH } from "app/config/routes";
import { NEW_AGENCY_ADDED } from "app/core/global-storage-constants";
import { IAgency } from "models";
import { AddNewAgencyForm } from "./components";
import {
  IAddNewAgentPageContent,
  getCancelAddWarningPopup,
  IAddAgencyFormContentKeys,
} from "./AddNewAgencyPageContent";
import {
  ITranslationContextProps,
  Translator,
  withTranslationF,
} from "contexts/TranslationContext";
import { GlobalEventTypes } from "contexts/GlobalContext";
import { FullPageSpinner } from "shared/modules/Common/FullPageSpinner";
import {
  IGlobalContextProps,
  withGlobalContext,
} from "shared/contexts/GlobalContext";
import { PageWrapper } from "components/common/PageWrapper";
import compose from "shared/utils/compose";
import { scrollToElement } from "shared/utils/dom";
import withBreadcrumbs from "app/hoc/withBreadcrumbs";

import "./AddNewAgencyPage.scss";
import { captureExceptionEvent } from "shared-services";

export interface IAddNewAgentPageProps
  extends ITranslationContextProps, RouterProps, IGlobalContextProps {}

interface IAddNewAgentPageState {
  content: IAddNewAgentPageContent;
  agency?: IAgency;
  isFormValid: boolean;
  isFormSubmitted: boolean;
  showLoader: boolean;
}

export class AddNewAgencyPage extends React.Component<
  IAddNewAgentPageProps,
  IAddNewAgentPageState
> {
  private addAgencyFormRef: React.RefObject<AddNewAgencyForm> =
    React.createRef<AddNewAgencyForm>();
  private isProcessStarted = false;

  constructor(props: IAddNewAgentPageProps) {
    super(props);

    this.state = {
      content: AddNewAgencyPage.getTranslation(props.translator),
      isFormValid: false,
      isFormSubmitted: false,
      showLoader: false,
    };
  }

  public render() {
    const { content, showLoader, agency, isFormSubmitted } = this.state;

    return (
      <PageWrapper
        header={content.header}
        buttons={[
          {
            text: content.close,
            onClick: this.closeForm,
          },
        ]}
      >
        <div className="p-add-new-agent-page">
          <div className="p-add-new-agent-page__form-description">
            {content.formDescription}
          </div>
          <AddNewAgencyForm
            ref={this.addAgencyFormRef}
            agency={agency}
            content={content}
            isFormSubmitted={isFormSubmitted}
            updateAgency={this.updateAgencyModel}
            updateFormValidity={this.updateFormValidity}
          />
          {this.renderFormButtons(content)}
          {showLoader && <FullPageSpinner />}
        </div>
      </PageWrapper>
    );
  }

  public static getDerivedStateFromProps(
    { translator }: IAddNewAgentPageProps,
    { content }: IAddNewAgentPageState
  ) {
    let stateUpdates = {};

    if (translator.version !== translator.getTranslationVersion(content)) {
      stateUpdates = { content: AddNewAgencyPage.getTranslation(translator) };
    }

    return stateUpdates;
  }

  private renderFormButtons = (content: IAddNewAgentPageContent) => {
    return (
      <div className="p-add-new-agent-page__buttons">
        <div className="p-add-new-agent-page__button">
          <Button
            type="secondary"
            onClick={this.closeForm}
            text={content.cancelAdd}
          />
        </div>
        <div className="p-add-new-agent-page__button">
          <Button
            type="primary"
            onClick={this.saveAgency}
            text={content.saveAgency}
          />
        </div>
      </div>
    );
  };

  private updateAgencyModel = (agency: IAgency) => this.setState({ agency });

  private updateFormValidity = (isFormValid: boolean) =>
    this.setState({ isFormValid });

  private saveAgency = async () => {
    const { isFormValid, agency } = this.state;
    this.setState({ isFormSubmitted: true });

    if (!this.isProcessStarted && isFormValid && agency) {
      this.isProcessStarted = true;
      this.setState({ showLoader: true });

      try {
        await AgencyService.createAgency(agency);

        this.setupAgencyAddedInfo();
        this.returnToAgenciesList();
      } catch (error) {
        captureExceptionEvent(error);

        this.setState({ showLoader: false });
        this.isProcessStarted = false;
      }
    }
  };

  private setupAgencyAddedInfo = () =>
    GlobalStorage.updateValue(NEW_AGENCY_ADDED, true);

  private returnToAgenciesList = () => {
    scrollToElement(".a-master-page__hidden-panel", { block: "end" });
    this.props.router.navigate(PROFILE_CONTACT_FULL_PATH);
  };

  private closeForm = () => {
    const { globalContext } = this.props;
    const { content, agency } = this.state;

    if (agency && Object.keys(agency).some((field) => agency[field])) {
      const closePopup = () =>
        globalContext.notifyListener(GlobalEventTypes.closeAllGlobalAlert);
      const stopEditing = () => {
        closePopup();
        this.returnToAgenciesList();
      };

      const warningPopupSettings = {
        content: content.cancelAddWarning,
        continueCanceling: closePopup,
        closePopup: stopEditing,
      };

      const message = getCancelAddWarningPopup(warningPopupSettings);

      globalContext.notifyListener(
        GlobalEventTypes.notifyingGlobalAlert,
        message
      );
    } else {
      this.returnToAgenciesList();
    }
  };

  private static getTranslation = (translator: Translator) =>
    translator.compileTranslationObject<IAddNewAgentPageContent>(
      IAddAgencyFormContentKeys,
      "common"
    );
}

export default compose(
  withTranslationF("addContactPage"),
  withBreadcrumbs,
  withGlobalContext,
  withRouter
)(AddNewAgencyPage);
